import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, Link2Off, Calculator } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LocaleLink } from '@/components/LocaleLink';
import { useSeoMeta, useNoIndex } from '@/hooks/usePageTitle';
import { SharedComparisonView } from '@/components/scenarios/SharedComparisonView';
import { Brand, ActionLink } from '@/components/visual/PagePrimitives';
import { trackProductEvent } from '@/lib/analytics';
import logger from '@/lib/logger';

/**
 * Public, read-only scenario comparison (product-led acquisition surface).
 * No account needed to view; nothing here reveals the owner or their account.
 */
export default function SharedScenarioComparisonPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  useSeoMeta(t('sharedScenarios.metaTitle'), t('sharedScenarios.metaDescription'));
  useNoIndex();

  useEffect(() => {
    let cancelled = false;
    axios.get(`/api/shared/scenarios/${token}`)
      .then(res => {
        if (cancelled) return;
        setData(res.data);
        setState('ready');
      })
      .catch(err => {
        logger.debug('[shared scenarios] unavailable:', err?.message || err);
        if (!cancelled) setState('unavailable');
      });
    return () => { cancelled = true; };
  }, [token]);

  const generated = data?.generated_at ? new Date(data.generated_at) : null;
  const title = data?.decision?.title || t('sharedScenarios.defaultTitle');

  return (
    <div className="product-ui board-view">
      <header className="board-header">
        <div className="site-container board-header-inner">
          <Brand testId="shared-brand-link" />
            <ActionLink to="/calculator"
              variant="outline"
              className="text-xs sm:text-sm"
              onClick={() => trackProductEvent('shared_page_calculator_cta_clicked', {
                source: 'shared_page', share_ref: data?.share_ref,
              })}
              data-testid="shared-header-cta"
            >
              <Calculator className="w-4 h-4 sm:mr-2 shrink-0" aria-hidden="true" />
              <span>{t('homepage.primaryAction')}</span>
            </ActionLink>
        </div>
      </header>

      <main className="site-container board-main">
        {state === 'loading' && (
          <div className="flex items-center gap-2 py-16 text-sm text-[#66707A]" data-testid="shared-loading">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> {t('sharedScenarios.loading')}
          </div>
        )}

        {state === 'unavailable' && (
          <div className="mx-auto max-w-lg rounded-lg border border-[#DDE1E5] bg-white px-6 py-12 text-center"
               data-testid="shared-unavailable">
            <Link2Off className="w-10 h-10 text-[#DDE1E5] mx-auto mb-4" aria-hidden="true" />
            <h1 className="text-2xl font-semibold tracking-tight text-[#171A1D]">
              {t('sharedScenarios.unavailableTitle')}
            </h1>
            <p className="mt-2 text-sm text-[#66707A]">{t('sharedScenarios.unavailableBody')}</p>
              <ActionLink to="/calculator" className="mt-6"
                      data-testid="shared-unavailable-cta">
                {t('homepage.primaryCta')}
              </ActionLink>
          </div>
        )}

        {state === 'ready' && data && (
          <>
            <div className="mb-6">
              {data.decision?.organisation && (
                <div className="text-[11px] uppercase tracking-[0.12em] text-[#66707A]"
                     style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                     data-testid="shared-organisation">
                  {data.decision.organisation}
                </div>
              )}
              <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-[#171A1D]"
                  data-testid="shared-title">
                {title}
              </h1>
              <p className="mt-2 text-xs text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                 data-testid="shared-meta">
                {generated && t('sharedScenarios.generated', { date: generated.toLocaleDateString() })}
                {' · '}{t('sharedScenarios.version', { version: data.version })}
                {' · '}{t('sharedScenarios.scenarioCount', { count: data.scenarios?.length || 0 })}
              </p>
            </div>

            <SharedComparisonView data={data} />
          </>
        )}
      </main>

      <footer className="board-footer">
        <div className="site-container board-footer-inner">
          <div>
            <div className="text-sm font-medium text-[#171A1D]" data-testid="shared-footer-brand">
              {t('sharedScenarios.footerBrand')}
            </div>
            <div className="text-xs text-[#66707A]">{t('sharedScenarios.footerTagline')}</div>
          </div>
            <ActionLink to="/calculator"
              className="h-10 bg-[#527F19] hover:bg-[#3F6114] text-white rounded-md"
              onClick={() => trackProductEvent('shared_page_calculator_cta_clicked', {
                source: 'shared_page', share_ref: data?.share_ref,
              })}
              data-testid="shared-footer-cta"
            >
              {t('homepage.primaryCta')}
            </ActionLink>
        </div>
      </footer>
    </div>
  );
}
